import React, { useEffect, useCallback, useMemo, useRef } from 'react';
import { Row, Col, Form } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useGetDataExtraction } from '@/hooks';
import { Spinner } from '@/components/Spinner';
import { StepsNavigation } from '@/components/ui/StepsNavigation';
import { useDataExtractionReport } from '@/context/DataExtractionReport';
import { GeneralInformation, Reports, Tools, ActionPlans } from './Steps';
import { getReportId } from '@/components/Settings/DataExtraction/utils/dataExtractionReports';
import {
	getHierarchyEndDateMoment,
	getHierarchyStartDateMoment
} from '@/components/Settings/DataExtraction/utils/hierarchyEndDate';
import { DIRECTION } from '@/components/ui/StepsNavigation/types';
import type { FormData, FormInitialValues } from './types';

const { useForm } = Form;

export function Content() {
	const [form] = useForm<FormData>();
	const isInitialized = useRef(false);

	const { dataExtractionReport, currentStep, parameters, onChangeStep, onSetDataExtractionReport } =
		useDataExtractionReport();

	const { isFetching, isSuccess, data } = useGetDataExtraction({
		organization_id: parameters.organization_id!,
		company_id: parameters.company_id!,
		data_extraction_id: parameters.id!
	});

	useEffect(() => {
		if (!data || !isSuccess) {
			return;
		}

		onSetDataExtractionReport(data);

		if (!isInitialized.current && data?.current_step?.sequence) {
			onChangeStep(data.current_step.sequence);
		}

	}, [data, isSuccess, onSetDataExtractionReport, onChangeStep]);

	const validateGeneralInformation = useCallback((): boolean => {
		const company_id = dataExtractionReport?.company_id;
		const organization_id = dataExtractionReport?.organization_id;
		const extraction_name = dataExtractionReport?.extraction_name;
		return !!(organization_id && company_id && extraction_name);
	}, [dataExtractionReport]);

	const handleOnClickStep = useCallback(
		(step: number): void => {
			if (!validateGeneralInformation()) {
				return;
			}
			onChangeStep(step);
		},
		[onChangeStep, validateGeneralInformation]
	);

	const initialValues = useMemo((): FormInitialValues | undefined => {
		if (!data) {
			return {
				general_information: {
					initial_date: getHierarchyStartDateMoment(),
					final_date: getHierarchyEndDateMoment()
				}
			};
		}

		const toolIds = data?.data_extraction_tools?.map((tool: any) => tool.ergonomic_tool_id) || [];

		return {
			general_information: {
				organization_id: data.organization_id,
				company_id: data.organization_id && data.company_id ? data.company_id : undefined,
				sector_id: data.sector_id,
				line_id: data.line_id,
				workstation_id: data.workstation_id,
				name: data.extraction_name,
				initial_date: getHierarchyStartDateMoment(data.start_date),
				final_date: getHierarchyEndDateMoment(data.end_date)
			},
			reports: {
				custom_report_type_id: data.custom_report_id,
				selected_reports: (data.data_extraction_reports?.map(getReportId).filter(Boolean) as string[]) || [],
				has_action_plan_extraction: Boolean(data.has_action_plan_extraction)
			},
			tools: {
				selected_tools: toolIds
			},
			action_plans: {
				selected_reports:
					data.data_extraction_action_plan_origins?.map((origin) => origin.custom_report_id) || [],
				selected_columns: Array.isArray(data.data_extraction_reports_action_plan_columns)
					? data.data_extraction_reports_action_plan_columns
					: data.data_extraction_reports_action_plan_columns?.columns || []
			}
		};
	}, [data]);

	useEffect(() => {
		if (initialValues && data && !isInitialized.current) {
			form.setFieldsValue(initialValues);
			handleOnClickStep(data.current_step?.sequence ?? 1);
			isInitialized.current = true;
		}
	}, [initialValues, form, data, handleOnClickStep]);

	if (parameters.id && isFetching) {
		return <Spinner size={48} />;
	}

	const steps = [
		{
			id: 'general_information',
			name: 'general_information',
			sequence: 1,
			description: I18n.get('General information'),
			component: <GeneralInformation />
		},
		{
			id: 'report',
			name: 'report',
			sequence: 2,
			description: I18n.get('Report'),
			component: <Reports />
		},
		{
			id: 'tools',
			name: 'tools',
			sequence: 3,
			description: I18n.get('Tools'),
			component: <Tools />
		},
		{
			id: 'action_plan',
			name: 'action_plan',
			sequence: 4,
			description: I18n.get('Action plan'),
			component: <ActionPlans />
		}
	];

	const currentStepComponent = steps[currentStep]?.component;

	return (
		<Form form={form} initialValues={initialValues}>
			<Row gutter={[0, 8]}>
				<Col span={24}>
					<StepsNavigation
						steps={steps}
						isComplete={false}
						currentStep={currentStep}
						direction={DIRECTION.HORIZONTAL}
						onStepNagivationClick={handleOnClickStep}
					/>
				</Col>
				<Col span={24}>{currentStepComponent}</Col>
			</Row>
		</Form>
	);
}
